import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services/messages';
import { ProjectContextService } from '@data-services/projects';
import { ProjectContext } from '@models/project-context';
import { ProjectUserRole } from '@models/projects';
import { customerMessagesUrlTree, isCustomerRole } from './customer-redirect';

export const projectUpdateAuthGuard: CanActivateFn = async (route, state) => {
  const dataContext = inject(DataContext);
  const messageService = inject(MessageService);
  const projectContextService = inject(ProjectContextService);
  const router = inject(Router);
  const translate = inject(TranslateService);

  const projectId = route.paramMap.get('projectId') || dataContext.projectContext()?.id;

  if (!projectId) {
    navigateToServices(router, messageService, translate, null);
    return false;
  }

  const cachedContext = dataContext.projectContext();

  if (!cachedContext?.currentUser) {
    return await loadContextAndCheckAccess(projectContextService, dataContext, router, messageService, translate, projectId);
  }

  if (isCustomerRole(cachedContext.currentUser.userRole)) {
    return customerMessagesUrlTree(router, projectId);
  }

  if (!hasUpdateAccess(cachedContext.currentUser.userRole)) {
    navigateToServices(router, messageService, translate, projectId);
    return false;
  }

  void refreshContextInBackground(projectContextService, dataContext, projectId);

  return true;
};

function hasUpdateAccess(role: ProjectUserRole): boolean {
  return role === 'Admin' || role === 'ServiceLeader';
}

async function loadContextAndCheckAccess(projectContextService: ProjectContextService, dataContext: DataContext, router: Router, messageService: MessageService, translate: TranslateService, projectId: string): Promise<boolean | UrlTree> {
  try {
    const projectContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);
    dataContext.setProjectContext(projectContext);

    if (isCustomerRole(projectContext.currentUser?.userRole)) {
      return customerMessagesUrlTree(router, projectId);
    }

    if (!projectContext.currentUser || !hasUpdateAccess(projectContext.currentUser.userRole)) {
      navigateToServices(router, messageService, translate, projectId);
      return false;
    }

    return true;
  } catch (error) {
    messageService.showErrorMessage(translate.instant('auth.errors.permissionCheckFailed'));
    void router.navigate(['/projects', projectId, 'services']);
    return false;
  }
}

async function refreshContextInBackground(projectContextService: ProjectContextService, dataContext: DataContext, projectId: string): Promise<void> {
  try {
    const projectContext: ProjectContext = await projectContextService.getProjectContextAsync(projectId);
    dataContext.setProjectContext(projectContext);
  } catch {
    // Silently fail - user already has cached context
  }
}

function navigateToServices(router: Router, messageService: MessageService, translate: TranslateService, projectId: string | null): void {
  messageService.showErrorMessage(translate.instant('auth.errors.projectUpdateAccessDenied'));
  
  if (projectId) {
    void router.navigate(['/projects', projectId, 'services']);
  } else {
    void router.navigate(['/projects']);
  }
}
